/**
 * `SettingsLlmsTxt` — Settings → AI Discovery panel (ADR-0206 Slice 4).
 *
 * Merchant-facing surface for the two site-scoped overrides on the
 * Sill-generated `llms.txt` served at
 * `https://edge.sill.so/v1/llms/{site_key_hash}.txt`:
 *
 *   - `llms_description`      — one-line blockquote tagline that
 *                                replaces the auto-generated default
 *                                (`AI-ready commerce endpoint for
 *                                {domain}.`). Cap: 280 chars.
 *   - `llms_custom_sections`  — free-form Markdown appended verbatim
 *                                after the auto-generated sections.
 *                                Cap: 2000 chars. Served as text/plain,
 *                                so no HTML rendering → no XSS surface.
 *
 * Save wiring (mirror of `SettingsGeneral`, ADR-0144):
 *   - Controlled inputs with dirty tracking; Save is disabled until
 *     something changed AND both fields fit under their length caps.
 *   - Save → `api.sites.update(site_id, { llms_description,
 *     llms_custom_sections })` and replaces the cached Site via
 *     `useSites().updateSite` so the LIST / detail views converge.
 *   - Empty string is the wire signal to CLEAR an override (server
 *     stores NULL, the edge generator falls back to the template).
 *   - Owner / admin only — reviewer / viewer see the read-only surface
 *     with no Save button (the server enforces the same gate on PATCH).
 *
 * Live preview:
 *   - A `<pre>` block below the form renders the exact llms.txt body
 *     the edge would serve for the current draft values, using the
 *     shared `generateLlmsTxtPreview` helper (byte-parity mirror of
 *     the edge generator, see `apps/dashboard/src/lib/llms-preview.ts`).
 *   - Purely client-side — no fetch to the edge on every keystroke.
 *     Debounced ~300 ms so a fast typist doesn't render on every
 *     keydown; the debounce is compute-shape, not rate-limit
 *     (`generateLlmsTxtPreview` is µs-cheap).
 *   - Skills feeding the preview come from
 *     `api.skills.listForSite(site_id)` on mount so the "Agent
 *     Capabilities (MCP)" section reflects the merchant's ACTUAL
 *     enabled skills.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';

import type {
  Site,
  SkillWithStatus,
  UserRole,
  VisibilityReport,
} from '@sill/types';
import type { InstallCoverage } from '@sill/types/api';

import { ApiError } from '../../api/http-client.js';
import { useApi } from '../../api/api-context.js';
import { useOptionalAuth } from '../../app/auth-context.js';
import { useSites, useToast } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import {
  generateLlmsTxtPreview,
  type LlmsPreviewCapability,
} from '../../lib/llms-preview.js';
import { derivePlatformKind } from '../../lib/platform-kind.js';
import { Card } from '../../components/atoms/index.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";
const serifFont = "'Instrument Serif', serif";

const LLMS_DESCRIPTION_MAX = 280;
const LLMS_CUSTOM_SECTIONS_MAX = 2000;
const PREVIEW_DEBOUNCE_MS = 300;

const LLMS_URL_BASE = 'https://edge.sill.so/v1/llms/';

export interface SettingsLlmsTxtProps {
  site: Site;
  /** Optional role override for tests that don't wire `<AuthProvider />`. */
  role_override?: UserRole;
}

export function SettingsLlmsTxt({
  site,
  role_override,
}: SettingsLlmsTxtProps): JSX.Element {
  const C = useC();
  const api = useApi();
  const auth = useOptionalAuth();
  const { updateSite } = useSites();
  const toast = useToast();

  const role: UserRole | null =
    role_override !== undefined
      ? role_override
      : auth !== null && auth.state.kind === 'authenticated'
        ? auth.state.user.role
        : null;
  const can_write = role === 'owner' || role === 'admin';

  const [draft_description, setDraftDescription] = useState<string>(
    site.llms_description ?? '',
  );
  const [draft_custom_sections, setDraftCustomSections] = useState<string>(
    site.llms_custom_sections ?? '',
  );
  const [pending, setPending] = useState<boolean>(false);
  const [url_copied, setUrlCopied] = useState<boolean>(false);

  // Skills feed the preview's "Agent Capabilities (MCP)" section. On
  // failure the preview falls back to an empty capabilities array (the
  // section is omitted, same as an unpublished site). The list is not
  // load-bearing for saving; if it never arrives the merchant can still
  // edit + save the two fields.
  const [preview_skills, setPreviewSkills] = useState<readonly LlmsPreviewCapability[]>([]);
  useEffect(() => {
    let cancelled = false;
    void api.skills
      .listForSite(site.site_id)
      .then((rows: SkillWithStatus[]) => {
        if (cancelled) return;
        setPreviewSkills(
          rows
            .filter((s) => s.enabled === true)
            .map((s) => ({ name: s.skill_id, description: s.description })),
        );
      })
      .catch(() => {
        // Silent — the preview just omits the capabilities section.
      });
    return () => {
      cancelled = true;
    };
  }, [api, site.site_id]);

  // Install coverage + visibility feed the "store vs website" decision
  // the generator reads via `is_store`. For a website (SaaS marketing,
  // blog, docs — no connected store connector and no search-eligible
  // catalog product) the preview omits the AI catalog + UCP profile
  // lines and the default description reads "Information and agent
  // endpoints for {domain}." instead of the commerce copy. Both reads
  // are soft — a failure falls through to the historical "store"
  // default so a merchant with slow APIs still sees a reasonable
  // preview.
  const [install_coverage, setInstallCoverage] =
    useState<InstallCoverage | null>(null);
  const [visibility_report, setVisibilityReport] =
    useState<VisibilityReport | null>(null);
  useEffect(() => {
    let cancelled = false;
    void api.sites
      .getInstallCoverage(site.site_id)
      .then((c) => {
        if (!cancelled) setInstallCoverage(c);
      })
      .catch(() => {
        // Silent — the preview falls back to is_store=true.
      });
    void api.visibility
      .getReport(site.site_id)
      .then((v) => {
        if (!cancelled) setVisibilityReport(v);
      })
      .catch(() => {
        // Silent — same fallback.
      });
    return () => {
      cancelled = true;
    };
  }, [api, site.site_id]);

  const is_store =
    derivePlatformKind({
      install: install_coverage,
      visibility: visibility_report,
    }) === 'store';

  // Dirty tracking — treat undefined and '' as equal (the wire omits
  // empty). A save is enabled only when at least one field's trimmed
  // value differs from the stored value.
  const cur_description = site.llms_description ?? '';
  const cur_custom_sections = site.llms_custom_sections ?? '';
  const description_dirty = draft_description !== cur_description;
  const custom_sections_dirty = draft_custom_sections !== cur_custom_sections;
  const description_over_cap = draft_description.length > LLMS_DESCRIPTION_MAX;
  const custom_sections_over_cap =
    draft_custom_sections.length > LLMS_CUSTOM_SECTIONS_MAX;
  const is_dirty =
    (description_dirty || custom_sections_dirty)
    && !description_over_cap
    && !custom_sections_over_cap;
  const save_disabled = !is_dirty || pending || !can_write;

  // Debounced preview input — the preview generator is deterministic
  // and cheap, but the panel re-renders on every keystroke otherwise.
  // 300 ms matches the human "typing pause" and keeps the visible
  // preview from flickering under fast typing.
  const [debounced_description, setDebouncedDescription] =
    useState<string>(draft_description);
  const [debounced_custom_sections, setDebouncedCustomSections] =
    useState<string>(draft_custom_sections);
  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedDescription(draft_description);
      setDebouncedCustomSections(draft_custom_sections);
    }, PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [draft_description, draft_custom_sections]);

  const site_key_hash =
    typeof site.site_key_hash === 'string' && site.site_key_hash.length > 0
      ? site.site_key_hash
      : '';
  const llms_url =
    site_key_hash.length > 0 ? `${LLMS_URL_BASE}${site_key_hash}.txt` : '';

  const preview_text = useMemo(() => {
    // A site with no known `site_key_hash` yet (pre-key-provision or
    // stale cache) still renders a preview against the placeholder hash
    // so the merchant can see what their edits will produce. The URL
    // block above already communicates when the endpoint isn't live yet.
    const hash_for_preview =
      site_key_hash.length > 0 ? site_key_hash : '{site_key_hash}';
    return generateLlmsTxtPreview({
      site_key_hash: hash_for_preview,
      domain: site.domain,
      display_name: site.name,
      capabilities: preview_skills,
      // The dashboard-side Site is always a real, DNS-proven domain
      // (the floor site_id.sites.sill.so never reaches Settings), so
      // the preview always emits the "## Website" line. Mirrors the
      // edge handler's `real_domain` wiring once a site is published.
      real_domain: true,
      is_store,
      // PAP is enabled on the production edge (PAP_PROTOCOL_ENABLED), so
      // the served file carries the PAP sign-in line; mirror it here.
      pap_enabled: true,
      // Empty string → let the generator fall back to its default
      // (matches server semantics: empty = clear override).
      ...(debounced_description.length > 0
        ? { description: debounced_description }
        : {}),
      ...(debounced_custom_sections.length > 0
        ? { custom_sections: debounced_custom_sections }
        : {}),
    });
  }, [
    site_key_hash,
    site.domain,
    site.name,
    preview_skills,
    debounced_description,
    debounced_custom_sections,
    is_store,
  ]);

  async function onCopyUrl(): Promise<void> {
    if (llms_url.length === 0) return;
    try {
      await navigator.clipboard.writeText(llms_url);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 1500);
    } catch {
      toast({ tone: 'coral', title: "Couldn't copy — long-press to select." });
    }
  }

  async function onSave(): Promise<void> {
    if (save_disabled) return;
    setPending(true);
    try {
      const patch: Partial<Site> = {};
      if (description_dirty) patch.llms_description = draft_description;
      if (custom_sections_dirty) patch.llms_custom_sections = draft_custom_sections;
      const updated = await api.sites.update(site.site_id, patch);
      updateSite(updated.site_id, updated);
      setDraftDescription(updated.llms_description ?? '');
      setDraftCustomSections(updated.llms_custom_sections ?? '');
      toast({ tone: 'lime', title: 'llms.txt saved.' });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.kind === 'forbidden') {
          toast({ tone: 'coral', title: 'Permission needed.' });
        } else if (err.kind === 'not_found') {
          toast({ tone: 'coral', title: 'Site not found — refresh the page.' });
        } else if (err.kind === 'bad_request') {
          toast({ tone: 'coral', title: 'Save rejected — check field lengths.' });
        } else {
          toast({ tone: 'coral', title: "Couldn't save — try again." });
        }
      } else {
        toast({ tone: 'coral', title: "Couldn't save — try again." });
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <Card className="p-5 sm:p-6 mb-5">
      <div className="mb-5 pb-4" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            marginBottom: 6,
          }}
        >
          AI DISCOVERY
        </div>
        <div
          style={{
            fontFamily: serifFont,
            fontSize: 22,
            color: C.text,
            letterSpacing: -0.5,
            marginBottom: 4,
          }}
        >
          Customize your llms.txt
        </div>
        <div
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            lineHeight: 1.5,
          }}
        >
          Sill serves a live <code style={{ fontFamily: monoFont }}>llms.txt</code>{' '}
          for this site — a plain-text summary agents read to understand your
          skills and endpoints. Everything below is optional; defaults
          derived from your site name and enabled skills apply when empty.
        </div>
      </div>

      <Row C={C} label="Endpoint" hint="The public URL agents fetch. Read-only.">
        {llms_url.length > 0 ? (
          <div className="flex items-center gap-2">
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 12,
                color: C.text,
                flex: 1,
                overflowWrap: 'anywhere',
              }}
            >
              {llms_url}
            </span>
            <button
              type="button"
              aria-label="Copy llms.txt URL"
              onClick={() => void onCopyUrl()}
              className="px-2 py-1 flex items-center gap-1"
              style={{
                background: C.surface2,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                fontFamily: monoFont,
                fontSize: 11,
                color: url_copied ? C.limeFg : C.text,
                letterSpacing: 1,
                cursor: 'pointer',
              }}
            >
              {url_copied ? <Check size={12} /> : <Copy size={12} />}
              {url_copied ? 'COPIED' : 'COPY'}
            </button>
          </div>
        ) : (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              fontStyle: 'italic',
            }}
          >
            Endpoint provisions once this site is verified.
          </div>
        )}
      </Row>

      <Row
        C={C}
        label="Description"
        hint="Shown as the blockquote line at the top of llms.txt. Auto-generated when empty."
      >
        <input
          aria-label="llms.txt description"
          value={draft_description}
          maxLength={LLMS_DESCRIPTION_MAX}
          readOnly={!can_write}
          disabled={pending}
          placeholder="Auto-generated from site name"
          onChange={(e) => setDraftDescription(e.target.value)}
          className="w-full px-3 py-2 outline-none"
          style={{
            fontFamily: sansFont,
            fontSize: 14,
            color: C.text,
            background: C.surface2,
            border: `1px solid ${description_over_cap ? C.coral : C.border}`,
            borderRadius: 2,
          }}
        />
        <CharCounter
          C={C}
          length={draft_description.length}
          max={LLMS_DESCRIPTION_MAX}
        />
      </Row>

      <Row
        C={C}
        label="Custom sections"
        hint="Markdown appended verbatim after the auto-generated sections. Served as plain text."
      >
        <textarea
          aria-label="llms.txt custom sections"
          value={draft_custom_sections}
          maxLength={LLMS_CUSTOM_SECTIONS_MAX}
          readOnly={!can_write}
          disabled={pending}
          rows={6}
          placeholder={
            '## Additional notes\n\nAny Markdown here is appended to your llms.txt.'
          }
          onChange={(e) => setDraftCustomSections(e.target.value)}
          className="w-full px-3 py-2 outline-none"
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: C.text,
            background: C.surface2,
            border: `1px solid ${custom_sections_over_cap ? C.coral : C.border}`,
            borderRadius: 2,
            resize: 'vertical',
            lineHeight: 1.5,
          }}
        />
        <CharCounter
          C={C}
          length={draft_custom_sections.length}
          max={LLMS_CUSTOM_SECTIONS_MAX}
        />
      </Row>

      <Row C={C} label="Preview" hint="How your llms.txt will render right now.">
        <pre
          aria-label="llms.txt preview"
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            color: C.text,
            background: C.surface2,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
            padding: 12,
            margin: 0,
            maxHeight: 320,
            overflow: 'auto',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.5,
          }}
        >
          {preview_text}
        </pre>
      </Row>

      {can_write ? (
        <div className="pt-5 flex justify-end">
          <button
            type="button"
            onClick={() => void onSave()}
            disabled={save_disabled}
            className="px-5 py-2"
            style={{
              background: C.lime,
              color: C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 13,
              borderRadius: 2,
              opacity: save_disabled ? 0.5 : 1,
              cursor: save_disabled ? 'not-allowed' : 'pointer',
            }}
          >
            {pending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      ) : null}
    </Card>
  );
}

function CharCounter({
  C,
  length,
  max,
}: {
  C: { muted: string; coralFg: string };
  length: number;
  max: number;
}): JSX.Element {
  const over = length > max;
  return (
    <div
      style={{
        fontFamily: monoFont,
        fontSize: 10,
        color: over ? C.coralFg : C.muted,
        letterSpacing: 1,
        marginTop: 4,
        textAlign: 'right',
      }}
    >
      {length} / {max}
    </div>
  );
}

function Row({
  C,
  label,
  hint,
  children,
}: {
  C: { text: string; muted: string; borderSoft: string };
  label: string;
  hint?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 py-4"
      style={{ borderBottom: `1px solid ${C.borderSoft}` }}
    >
      <div className="sm:col-span-1">
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 500 }}>
          {label}
        </div>
        {hint ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.muted,
              marginTop: 2,
              lineHeight: 1.5,
            }}
          >
            {hint}
          </div>
        ) : null}
      </div>
      <div className="sm:col-span-2">{children}</div>
    </div>
  );
}
